import React, { useState } from 'react';
import stripePromise from '../utils/stripe';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import '../utils/axiosConfig'; // Import to configure axios
import './Subscription.css';

interface SubscriptionProps {
  currentPlan: string;
}

const Subscription: React.FC<SubscriptionProps> = ({ currentPlan }) => {
  const [loading, setLoading] = useState<string | null>(null);
  const { user } = useAuth();

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '€9',
      features: ['500 API-Aufrufe/Monat', 'Aktuelles + letztes Jahr', '3 API-Schlüssel'],
      description: 'Perfekt für kleine Projekte und Tests',
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '€29',
      features: ['5.000 API-Aufrufe/Monat', 'Daten ab 2021', '5 API-Schlüssel'],
      description: 'Ideal für wachsende Unternehmen',
    },
    {
      id: 'business',
      name: 'Business',
      price: '€79',
      features: ['50.000 API-Aufrufe/Monat', 'Alle Funktionen', '10 API-Schlüssel'],
      description: 'Für Anwendungen mit hohem Volumen',
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '€149',
      features: ['Unbegrenzte API-Aufrufe', 'Premium-Support', '50 API-Schlüssel'],
      description: 'Maximale Leistung und Support',
    },
  ];

  const handlePlanChange = async (planId: string) => {
    if (!user) return;
    
    setLoading(planId);
    
    try {
      const response = await axios.post('/subscription/change-plan', {
        plan: planId,
      });
      
      // If response contains sessionId, redirect to Stripe Checkout (new subscription)
      if (response.data.sessionId) {
        const { sessionId } = response.data;
        const stripe = await stripePromise;
        
        if (stripe) {
          const { error } = await stripe.redirectToCheckout({ sessionId });
          if (error) {
            console.error('Stripe error:', error);
            alert('Zahlung fehlgeschlagen. Bitte versuchen Sie es erneut.');
          }
        } else {
          alert('Stripe ist nicht verfügbar. Bitte kontaktieren Sie den Support.');
        }
      } else if (response.data.success) {
        // Plan changed successfully (existing subscription modified)
        alert('Plan erfolgreich geändert! Die Seite wird neu geladen.');
        window.location.reload();
      }
    } catch (error: any) {
      console.error('Plan change error:', error);
      const errorMessage = error.response?.data?.error || 'Plan konnte nicht geändert werden. Bitte versuchen Sie es erneut.';
      alert(errorMessage);
    } finally {
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setLoading('portal');
    
    try {
      const response = await axios.post('/subscription/create-portal-session');
      const { url } = response.data;
      window.location.href = url;
    } catch (error) {
      console.error('Portal error:', error);
      alert('Abrechnungsportal konnte nicht geöffnet werden. Bitte versuchen Sie es erneut.');
      setLoading(null);
    }
  };

  const getPlanIndex = (plan: string) => {
    const planOrder = ['free', 'starter', 'professional', 'business', 'enterprise'];
    return planOrder.indexOf(plan);
  };

  const currentPlanIndex = getPlanIndex(currentPlan);

  return (
    <div className="subscription-section">
      <div className="section-header">
        <h2>Abonnement-Pläne</h2>
        <p>Wählen Sie den Plan, der am besten zu Ihren Bedürfnissen passt</p>
      </div>
      
      {currentPlan !== 'free' && (
        <div className="current-plan-info">
          <div className="current-plan-badge">
            <span>Aktueller Plan: <strong>{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}</strong></span>
          </div>
          <button 
            onClick={handleManageBilling}
            disabled={loading === 'portal'}
            className="subscription-btn subscription-btn-secondary"
          >
            {loading === 'portal' ? 'Lädt...' : 'Abrechnung verwalten'}
          </button>
        </div>
      )}
      
      <div className="plans-grid">
        {plans.map((plan) => {
          const planIndex = getPlanIndex(plan.id);
          const isCurrentPlan = currentPlan === plan.id;
          const isDowngrade = planIndex < currentPlanIndex;
          const isUpgrade = planIndex > currentPlanIndex;
          
          return (
            <div 
              key={plan.id} 
              className={`plan-card ${isCurrentPlan ? 'current' : ''} ${isUpgrade ? 'upgrade' : ''}`}
            >
              <div className="plan-header">
                <h3>{plan.name}</h3>
                <div className="plan-price">
                  <span className="price">{plan.price}</span>
                  <span className="period">/Monat</span>
                </div>
                <p className="plan-description">{plan.description}</p>
              </div>
              
              <div className="plan-features">
                <ul>
                  {plan.features.map((feature, index) => (
                    <li key={index}>
                      <span className="feature-check">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="plan-action">
                {isCurrentPlan ? (
                  <button className="subscription-btn subscription-btn-current" disabled>
                    Aktueller Plan
                  </button>
                ) : isDowngrade ? (
                  <button className="subscription-btn subscription-btn-disabled" disabled>
                    Support kontaktieren
                  </button>
                ) : (
                  <button
                    onClick={() => handlePlanChange(plan.id)}
                    disabled={loading === plan.id}
                    className="subscription-btn subscription-btn-primary"
                  >
                    {loading === plan.id ? 'Lädt...' : 'Upgraden'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="subscription-notes">
        <p><strong>Hinweis:</strong> Alle Pläne beinhalten sicheren API-Zugang, detaillierte Nutzungsanalysen und E-Mail-Support.</p>
        <p>Möchten Sie downgraden? Kontaktieren Sie unser Support-Team für Unterstützung.</p>
      </div>
    </div>
  );
};

export default Subscription;
